import { faBan, faShelves } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { isNonEmptyNumber, numberFormat, useAccountId } from '@labrav/common'
import {
  ButtonV2,
  ModalConfirmV2,
  ModalSizeVariants,
  VerticalDivider,
} from '@labrav/react-components'
import { get, isEmpty, noop } from 'lodash'
import { useMemo, useState, type Dispatch, type SetStateAction } from 'react'
import { useDispatch } from 'react-redux'

import { useFlyOutProductsBasedOnSelectedCloud } from '../../common/utils/hooks/useFlyOutProductsBasedOnSelectedCloud'
import { deactivateFreeTrialV2 } from '../../modules/freeTrials/action'
import { Product } from '../../modules/productsListing/types'
import {
  DetailInfo,
  DetailRow,
} from '../../common/components/DetailRow/DetailRow'
import { productDetailDate } from '../../common/utils/productDetailDate'
import { useTimeZone } from '../../common/utils/hooks/useTimeZone'
import { DeActivateModal } from './DeActivateModal'
import { useStyles } from './ViewFreeTrial.styles'

export type ViewFreeTrialProps = {
  selectedProductId: string
  openViewFreeTrialModal: boolean
  setOpenViewFreeTrialModal: Dispatch<SetStateAction<boolean>>
  successDeactivateFreeTrial: () => void
}

export const ViewFreeTrial = ({
  openViewFreeTrialModal,
  setOpenViewFreeTrialModal,
  selectedProductId,
  successDeactivateFreeTrial,
}: ViewFreeTrialProps) => {
  const { classes } = useStyles()
  const dispatch = useDispatch()
  const accountId = useAccountId({ product: 'flyout' })
  const { timeZone } = useTimeZone()
  const { products } = useFlyOutProductsBasedOnSelectedCloud()
  const [openConfirmModal, setOpenConfirmModal] = useState(false)
  const handleOpenConfirmModal = () => {
    setOpenConfirmModal(true)
  }
  const handleCloseConfirmModal = () => {
    setOpenConfirmModal(false)
  }
  const deactivateCallBack = () => {
    setOpenConfirmModal(false)
    successDeactivateFreeTrial()
  }
  const handleDeactivate = async () => {
    if (accountId) {
      await dispatch(
        deactivateFreeTrialV2({
          labraFreeTrialId: product?.freeTrial?.labraFreeTrialId || '',
          partnerId: accountId,
          productId: product?.productId || '',
          cloudMarketplace: product?.cloudMarketplace || '',
          callBack: deactivateCallBack,
        }) as never
      )
    }
  }
  const product =
    products.find(item => item.productId === selectedProductId) ||
    ({} as Product)

  const selectedDimensions = useMemo(
    () =>
      (
        (product?.productDimensions || []).filter(
          dimension => dimension?.freeTrial?.freeTrial
        ) || []
      ).map(dimension => ({
        id: dimension.id,
        label: dimension?.label,
        quantity: Number(dimension?.freeTrial?.quantity),
      })),
    [product]
  )
  const selectedFreeTrialProduct = useMemo(
    () => ({
      productId: product?.productId || '',
      eulaType: product?.freeTrial?.eulaType || '',
      eulaFile: product?.freeTrial?.eulaFiles || [],
      freeTrial: {
        ...product?.freeTrial,
      },
      productDimensions: [...selectedDimensions],
    }),
    [product, selectedDimensions]
  )

  const { modifiedDate: createdAtDate, modifiedTime: createdAtTime } =
    selectedFreeTrialProduct?.freeTrial?.freeTrialEnabledAt
      ? productDetailDate(
          selectedFreeTrialProduct?.freeTrial?.freeTrialEnabledAt,
          timeZone
        )
      : { modifiedDate: '', modifiedTime: '' }

  const handleCloseViewFreeTrialModal = () => {
    setOpenViewFreeTrialModal(false)
  }

  const dimensionsList = (selectedDimensions || []).map((dimension, index) => (
    <DetailRow
      key={dimension.id || index}
      borderBottom={index + 1 !== (selectedDimensions || []).length}
      children={
        <>
          <DetailInfo
            value={dimension?.label ? dimension?.label : '-'}
            keyName="dimension-label"
            className={classes.dimensionKeyValue}
          />
          <DetailInfo
            keyName="dimension-price"
            className={classes.dimensionKeyValue}
            value={
              isNonEmptyNumber(get(dimension, 'quantity') as number)
                ? `${numberFormat(get(dimension, 'quantity') as number)}`
                : '-'
            }
          />
        </>
      }
    />
  ))
  const openEula = (eulaFiles?: string[]) => () => {
    if (!isEmpty(eulaFiles)) {
      eulaFiles?.forEach(file => {
        const newWind = window.open(file, '_blank', 'noopener noreferrer')
        if (newWind) {
          newWind.opener = null
        }
      })
    }
  }
  return (
    <>
      <ModalConfirmV2
        maxWidth={'800px'}
        title={'Free trial '}
        open={openViewFreeTrialModal}
        content={
          <>
            <div
              className={classes.freeTrialWrapper}
              data-testid={'view-free-trial'}
            >
              <div
                className={classes.root}
                data-testid={'dimesnions-free-trial'}
              >
                <DetailRow
                  borderBottom
                  children={
                    <>
                      <DetailInfo title="Dimension" />
                      <DetailInfo title="Quantity" />
                    </>
                  }
                />
                {dimensionsList}
              </div>
              <div>
                <DetailRow
                  borderBottom
                  className={classes.eulaSection}
                  children={
                    <>
                      <DetailInfo
                        title="EULA"
                        keyName="eulaType"
                        value={
                          <div className={classes.customWrapper}>
                            {selectedFreeTrialProduct?.eulaType}{' '}
                            {selectedFreeTrialProduct?.eulaFile &&
                              !!selectedFreeTrialProduct?.eulaFile?.length && (
                                <>
                                  <VerticalDivider height="24px" />
                                  <ButtonV2
                                    styleType="link"
                                    onClick={openEula(
                                      selectedFreeTrialProduct?.eulaFile
                                    )}
                                  >
                                    Download EULA
                                  </ButtonV2>
                                </>
                              )}
                          </div>
                        }
                      />
                      <DetailInfo
                        title="Created on"
                        value={
                          <div className={classes.createdAtWrapper}>
                            <div className={classes.createdAtDate}>
                              {createdAtDate}
                            </div>
                            <div className={classes.createdAtTime}>
                              {createdAtTime}
                            </div>
                          </div>
                        }
                        keyName={'createdAtDate'}
                      />
                    </>
                  }
                />
                <DetailRow
                  className={classes.numberOfDaysSection}
                  children={
                    <>
                      <DetailInfo
                        title="Time duration"
                        keyName="numberOfDays"
                        value={
                          <div className={classes.customWrapper}>
                            {
                              selectedFreeTrialProduct?.freeTrial
                                ?.freeTrialExpiresInDays
                            }
                            {' Day(s)'}
                          </div>
                        }
                      />
                    </>
                  }
                />
              </div>
              <div>
                <ButtonV2
                  data-testid={'deactivate-free-trial'}
                  styleType="link"
                  onClick={handleOpenConfirmModal}
                >
                  <FontAwesomeIcon
                    icon={faBan}
                    style={{
                      width: '20px',
                      height: '16px',
                      paddingRight: '5px',
                    }}
                  />
                  Deactivate free trial
                </ButtonV2>
              </div>
            </div>
          </>
        }
        onDecline={handleCloseViewFreeTrialModal}
        variant={ModalSizeVariants.xLarge}
        icon={
          <FontAwesomeIcon
            icon={faShelves}
            style={{ width: '20px', height: '16px' }}
          />
        }
        hideCancelIcon={false}
        hideCancelButton={true}
        hideAcceptButton={true}
        onAccept={noop}
      />
      <DeActivateModal
        handleCloseConfirmModal={handleCloseConfirmModal}
        handleDeactivate={handleDeactivate}
        openConfirmModal={openConfirmModal}
      />
    </>
  )
}
